
import React, { useEffect, useState } from "react";
import moment from "moment-timezone";
import { CalendarEvent, ChevronLeft, ChevronRight, Clock } from "react-bootstrap-icons";
import { BsFillCalendar2DateFill } from "react-icons/bs";
import InputField from "../common/InputField";
import { Badge, Button, Card, Form } from "react-bootstrap";
import { errorAlert } from "../common/Alert";
import ExtendParking from "./ExtendParking";

const ScheduleSection = ({
  isExtendParking,
  extendBookingData,
  locationDetail,
  inData,
  setInData,
  setConfirm,
  selectedDates,
  setSelectedDates,
  selectedBookingType,
  setSelectedBookingType,
  isMonthlySubscription,
  setIsMonthlySubscription,
}) => {

  const [activeTab, setActiveTab] = useState("");
  const [extensionDuration, setExtensionDuration] = useState();
  const [currentDate, setCurrentDate] = useState(new Date());



  const [range, setRange] = useState({
    start: null,
    end: null,
  });

  const today = new Date();

  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // ================================
  // CALENDAR
  // ================================

  const getDaysInMonth = (date) => {

    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();

    let calendar = [];
    for (let i = 0; i < firstDay; i++) {
      calendar.push(null);
    }

    for (let i = 1; i <= totalDays; i++) {
      calendar.push(i);
    }

    return calendar;
  };

  const calendarDays = getDaysInMonth(currentDate);

  const changeMonth = (dir) => {
    const newDate = new Date(currentDate);

    newDate.setMonth(newDate.getMonth() + dir);

    setCurrentDate(newDate);
  };

  const getFullDate = (day) => {
    return new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
  };





  // ================================
  // DISABLE PAST DATE
  // ================================

  const isPastDate = (day) => {
    if (!day) return false;

    const d = getFullDate(day);

    return d < new Date(today.setHours(0, 0, 0, 0));
  };


  // ================================
  // DISABLE PAST DATES
  // ================================
  const disablePastDates = ({ date }) => {
    const today = new Date();

    // Check if the date is in the past
    const isPastDate = date < today.setHours(0, 0, 0, 0);

    // Check if the date falls within any already selected date range
    // Uses selectedDates which now stores {startDateTime, endDateTime} objects
    const isBooked = selectedDates.some((booking) => {
      const startDate = new Date(booking.startDateTime);
      const endDate = new Date(booking.endDateTime);
      const normalizedDate = new Date(new Date(date).setHours(0, 0, 0, 0));
      const normalizedStart = new Date(startDate.setHours(0, 0, 0, 0));
      const normalizedEnd = new Date(endDate.setHours(0, 0, 0, 0));
      return normalizedDate >= normalizedStart && normalizedDate <= normalizedEnd;
    });

    return isPastDate || isBooked;
  };


  // ================================
  // DATE SELECT HANDLER
  // ================================
  const handleDateSelect = (startDate) => {

    const tz = locationDetail?.location_timezone || moment.tz.guess();
    const clickedDateStr = moment(getFullDate(startDate)).format("YYYY-MM-DD");
    const todayStr = moment().tz(tz).format("YYYY-MM-DD");
    const isToday = clickedDateStr === todayStr;

    // ← if today use current time, else use 12:00 AM or selected time from picker
    const resolvedStartTime = isToday
      ? moment().tz(tz).format("hh:mm A")  // current time
      : inData.startTime !== moment().tz(tz).format("hh:mm A")
        ? inData.startTime                  // user selected from timepicker
        : "12:00 AM";                       // default 12:00 AM

    // ================= DAY =================
    if (selectedBookingType === "DAY") {

      const clickedDate = getFullDate(startDate);

      const startMoment = moment.tz(
        `${clickedDateStr} ${resolvedStartTime}`,
        "YYYY-MM-DD hh:mm A",
        tz
      );

      const endMoment = moment.tz(
        `${clickedDateStr} ${inData.endTime}`,
        "YYYY-MM-DD hh:mm A",
        tz
      ).seconds(59).milliseconds(999);

      const bookingObject = {
        startDateTime: startMoment.utc().toISOString(),
        endDateTime: endMoment.utc().toISOString(),
      };

      const isAlreadySelected = selectedDates.some(
        (date) => moment(date.startDateTime).format("YYYY-MM-DD") === moment(bookingObject.startDateTime).format("YYYY-MM-DD")
      );

      let updatedDates = [...selectedDates];

      if (isAlreadySelected) {
        updatedDates = updatedDates.filter(
          (date) => date.startDateTime !== bookingObject.startDateTime
        );
      } else {
        if (isExtendParking && updatedDates.length >= 6) {
          errorAlert({ message: "You cannot select more than 6 dates when extending parking." });
          return;
        }
        updatedDates.push(bookingObject);
      }

      if (updatedDates.length > 6) {
        setSelectedDates([]);
        setSelectedBookingType("WEEKLY");
        setActiveTab("Weekly");
      } else {
        setSelectedDates(updatedDates);
      }
    }


    // ================= WEEKLY =================
    else if (selectedBookingType === "WEEKLY") {
      const start = getFullDate(startDate);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);

      const newWeekDates = [];
      let current = new Date(start);
      while (current <= end) {
        newWeekDates.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }

      // Check if clicked date falls within an already selected week range
      const overlappingIndex = selectedDates.findIndex((selected) => {
        const selectedStart = moment(selected.startDateTime);
        const selectedEnd = moment(selected.endDateTime);
        return moment(start).isBetween(selectedStart, selectedEnd, "day", "[]");
      });

      // Only check booked dates if NOT within an existing selected range
      if (overlappingIndex === -1) {
        const isAnyDateBooked = newWeekDates.some((date) =>
          selectedDates?.some(
            (booked) =>
              moment(booked.startDateTime).format("YYYY-MM-DD") ===
              moment(date).format("YYYY-MM-DD")
          )
        );
        if (isAnyDateBooked) {
          errorAlert({ message: "Cannot select this week as it contains a booked date." });
          return;
        }
      }

      const startMoment = moment.tz(
        `${clickedDateStr} ${resolvedStartTime}`,
        "YYYY-MM-DD hh:mm A",
        tz
      );

      const endMoment = moment.tz(
        `${moment(end).format("YYYY-MM-DD")} ${inData.endTime}`,
        "YYYY-MM-DD hh:mm A",
        tz
      ).seconds(59).milliseconds(999);

      const bookingObject = {
        startDateTime: startMoment.utc().toISOString(),
        endDateTime: endMoment.utc().toISOString(),
      };

      let updatedWeeks = [...selectedDates];

      if (overlappingIndex !== -1) {
        const isSameStart =
          moment(selectedDates[overlappingIndex].startDateTime).format("YYYY-MM-DD") ===
          moment(start).format("YYYY-MM-DD");

        if (isSameStart) {
          // Clicked same start date → deselect
          updatedWeeks = updatedWeeks.filter((_, i) => i !== overlappingIndex);
        } else {
          // Clicked within range → update to new start + 6 days
          const newEnd = new Date(start);
          newEnd.setDate(start.getDate() + 6);

          const newStartMoment = moment.tz(
            `${clickedDateStr} ${resolvedStartTime}`,
            "YYYY-MM-DD hh:mm A",
            tz
          );

          const newEndMoment = moment.tz(
            `${moment(newEnd).format("YYYY-MM-DD")} ${inData.endTime}`,
            "YYYY-MM-DD hh:mm A",
            tz
          ).seconds(59).milliseconds(999);

          updatedWeeks[overlappingIndex] = {
            ...updatedWeeks[overlappingIndex],
            startDateTime: newStartMoment.utc().toISOString(),
            endDateTime: newEndMoment.utc().toISOString(),
          };
        }
      } else {
        // Outside any range → add as new week
        updatedWeeks.push(bookingObject);
      }

      if (isExtendParking && updatedWeeks.length > 3) {
        errorAlert({ message: "You cannot select more than 3 weeks when extending parking." });
        return;
      }

      if (updatedWeeks.length > 3) {
        setSelectedBookingType("MONTHLY");
        setSelectedDates([]);
        setActiveTab("Monthly");
      } else {
        setSelectedDates(updatedWeeks);
      }
    }


    // ================= MONTHLY =================
    else if (selectedBookingType === "MONTHLY") {
      const start = getFullDate(startDate);
      const end = new Date(start);
      end.setMonth(start.getMonth() + 1);

      // Check if clicked date falls within already selected month range
      const overlappingIndex = selectedDates.findIndex((selected) => {
        const selectedStart = moment(selected.startDateTime);
        const selectedEnd = moment(selected.endDateTime);
        return moment(start).isBetween(selectedStart, selectedEnd, "day", "[]");
      });

      if (overlappingIndex !== -1) {
        const isSameStart =
          moment(selectedDates[overlappingIndex].startDateTime).format("YYYY-MM-DD") ===
          moment(start).format("YYYY-MM-DD");

        if (isSameStart) {
          // Clicked same start date (e.g. 5 june again) → deselect
          setSelectedDates([]);
        } else {
          // Clicked within range (e.g. 6 june) → update start to 6 june, end to 6 july
          const newEnd = new Date(start);
          newEnd.setMonth(start.getMonth() + 1);

          const newStartMoment = moment.tz(
            `${clickedDateStr} ${resolvedStartTime}`,
            "YYYY-MM-DD hh:mm A",
            tz
          );

          const newEndMoment = moment.tz(
            `${moment(newEnd).format("YYYY-MM-DD")} ${inData.endTime}`,
            "YYYY-MM-DD hh:mm A",
            tz
          ).seconds(59).milliseconds(999);

          setSelectedDates([{
            startDateTime: newStartMoment.utc().toISOString(),
            endDateTime: newEndMoment.utc().toISOString(),
          }]);
        }
        return;
      }

      // Outside selected range — check for booked dates
      const hasBookedDate = selectedDates.some((booking) => {
        const bookingStart = new Date(booking.startDateTime);
        const bookingEnd = new Date(booking.endDateTime);
        let check = new Date(start);
        while (check <= end) {
          if (check >= bookingStart && check <= bookingEnd) return true;
          check.setDate(check.getDate() + 1);
        }
        return false;
      });

      if (hasBookedDate) {
        errorAlert({ message: "Cannot select this month as it contains a booked date." });
        return;
      }

      const startMoment = moment.tz(
        `${clickedDateStr} ${resolvedStartTime}`,
        "YYYY-MM-DD hh:mm A",
        tz
      );

      const endMoment = moment.tz(
        `${moment(end).format("YYYY-MM-DD")} ${inData.endTime}`,
        "YYYY-MM-DD hh:mm A",
        tz
      ).seconds(59).milliseconds(999);

      setSelectedDates([{
        startDateTime: startMoment.utc().toISOString(),
        endDateTime: endMoment.utc().toISOString(),
      }]);
    }
  };


  // ================================
  // EXTEND PARKING — AUTO SET DATES
  // ================================
  useEffect(() => {
    if (!isExtendParking || !extendBookingData?.bookingDates?.length) return;

    const bookingDates = extendBookingData.bookingDates;
    const lastEndDate = bookingDates[bookingDates.length - 1]?.reservationEndDate;
    if (!lastEndDate) return;

    const tz = locationDetail?.location_timezone || moment.tz.guess();
    const lastEndMoment = moment.utc(lastEndDate).tz(tz);

    // Start = last end date + 1 day at 12:00 AM in LOCATION timezone
    const startDateStr = lastEndMoment.clone().add(1, "day").format("YYYY-MM-DD");

    // Start = last end date + 1 day at 12:00 AM in LOCATION timezone
    const startMoment = moment.tz(
      `${startDateStr} 12:00 AM`,
      "YYYY-MM-DD hh:mm A",
      tz
    );

    // ================= MONTHLY SUBSCRIPTION =================
    if (isMonthlySubscription) {
      setSelectedBookingType("MONTHLY_SUBSCRIPTION");

      const endMoment = moment.tz(
        `${startDateStr} ${inData.endTime}`,
        "YYYY-MM-DD hh:mm A",
        tz
      ).add(1, "month").endOf("day");

      setSelectedDates([
        {
          startDateTime: startMoment.clone().utc().toISOString(),
          endDateTime: endMoment.utc().toISOString(),
        },
      ]);
      return;
    }

    // ================= DURATION BASED =================
    if (!extensionDuration) return;

    let endDateStr;

    if (extensionDuration === "1_DAY") {
      endDateStr = lastEndMoment.clone().add(1, "day").format("YYYY-MM-DD"); // same day as start
    } else if (extensionDuration === "3_DAY") {
      endDateStr = lastEndMoment.clone().add(3, "days").format("YYYY-MM-DD");
    } else if (extensionDuration === "WEEK") {
      endDateStr = lastEndMoment.clone().add(7, "days").format("YYYY-MM-DD");
    } else {
      return;
    }

    // ✅ Same as handleDateSelect — use inData.endTime with location tz
    const endMoment = moment.tz(
      `${endDateStr} ${inData.endTime}`,
      "YYYY-MM-DD hh:mm A",
      tz
    ).seconds(59).milliseconds(999);

    const bookingObject = {
      startDateTime: startMoment.utc().toISOString(),
      endDateTime: endMoment.utc().toISOString(),
    };

    setSelectedDates([bookingObject]);

  }, [extensionDuration, isExtendParking, isMonthlySubscription, extendBookingData?.bookingDates, inData.startTime, inData.endTime]);


  // ================================
  // RANGE UI
  // ================================

  const isInRange = (day) => {
    if (!day || !Array.isArray(selectedDates) || selectedDates.length === 0) {
      return false;
    }

    const current = moment(getFullDate(day)).startOf("day");

    return selectedDates.some((booking) => {
      if (!booking?.startDateTime || !booking?.endDateTime) return false;

      const tz = locationDetail?.location_timezone || moment.tz.guess();

      const start = moment.utc(booking.startDateTime).tz(tz).startOf("day");
      const end = moment.utc(booking.endDateTime).tz(tz).startOf("day");
      //                                             ↑
      // .tz() use karo — location timezone mein convert karo
      // "11:59 PM US/Central" UTC se wapis US/Central → same day rahega
      // .local() (IST) use karne se next day ban jaata tha

      return current.isBetween(start, end, "day", "[]");
    });
  };

  const isStartOrEnd = (day) => {
    if (!day || !Array.isArray(selectedDates) || selectedDates.length === 0) {
      return false;
    }

    const current = moment(getFullDate(day)).startOf("day");

    return selectedDates.some((booking) => {
      if (!booking?.startDateTime || !booking?.endDateTime) return false;

      const tz = locationDetail?.location_timezone || moment.tz.guess();

      const start = moment.utc(booking.startDateTime).tz(tz).startOf("day");
      const end = moment.utc(booking.endDateTime).tz(tz).startOf("day");

      return current.isSame(start, "day") || current.isSame(end, "day");
    });
  };




  // ================================
  // TIME
  // ================================

  const convertTo24 = (time12) => {
    if (!time12) return "";

    const [time, modifier] = time12.split(" ");

    let [hours, minutes] = time.split(":");

    hours = parseInt(hours);

    if (modifier === "PM" && hours !== 12) {
      hours += 12;
    }

    if (modifier === "AM" && hours === 12) {
      hours = 0;
    }

    return `${String(hours).padStart(2, "0")}:${minutes}`;
  };

  const convertTo12 = (time24) => {
    let [hours, minutes] = time24.split(":");

    let modifier = "AM";

    hours = parseInt(hours);

    if (hours >= 12) {
      modifier = "PM";
      if (hours > 12) {
        hours -= 12;
      }
    }

    if (hours === 0) {
      hours = 12;
    }

    return `${String(hours).padStart(2, "0")}:${minutes} ${modifier}`;
  };


  const handleTimeChange = (e) => {
    const { name, value } = e.target;

    setInData((prev) => {
      const updatedData = {
        ...prev,
        [name]: convertTo12(value),
      };

      // Auto-calculate end time when start time changes
      if (name === "startTime") {
        const [hour, minute] = value.split(":");
        let newHour = parseInt(hour, 10);

        // Add 24 hours (wraps within same day) — keeps end time = start + 24h logic
        newHour = (newHour + 24) % 24;

        // If minutes are not :00, bump end hour by 1 more
        let endHour = newHour;
        if (minute !== "00") {
          endHour = (newHour + 1) % 24;
        }

        const endPeriod = endHour >= 12 ? "PM" : "AM";
        const formattedEndHour = endHour % 12 === 0 ? 12 : endHour % 12;

        // Uncomment below to enable dynamic end time calculation:
        // updatedData.endTime = `${String(formattedEndHour).padStart(2, "0")}:00 ${endPeriod}`;
      }

      return updatedData;
    });
  };




  // ================================
  // INIT START TIME from timezone
  // ================================

  const setInitialStartTime = () => {
    const currentTimeZone =
      locationDetail?.location_timezone ?? moment.tz.guess();
    const currentTime = moment().tz(currentTimeZone).format("hh:mm A");
    setInData((prev) => ({ ...prev, startTime: currentTime }));
  };



  // ================================
  // MONTHLY SUBSCRIPTION
  // ================================

  const handleMonthlySubscriptionClick = () => {
    setIsMonthlySubscription(true);
    setSelectedBookingType("MONTHLY_SUBSCRIPTION");
    const timezone = locationDetail?.location_timezone || moment.tz.guess();
    const now = moment.tz(timezone);
    let selectedDatesInUtc = [
      {
        startDateTime: moment.tz(
          `${now.format("YYYY-MM-DD")} 12:00 AM`,
          "YYYY-MM-DD hh:mm A",
          timezone
        ).seconds(0).milliseconds(0).utc().toISOString(),

        endDateTime: moment.tz(
          `${now.clone().add(1, "month").endOf("day").format("YYYY-MM-DD")} ${inData?.endTime}`,
          "YYYY-MM-DD hh:mm A",
          timezone
        ).seconds(59).milliseconds(999).utc().toISOString(),
      },
    ];

    setSelectedDates(selectedDatesInUtc);
  };

  const handleCancelSubscriptionClick = () => {
    setIsMonthlySubscription(false);
    setSelectedBookingType("DAY");
    setActiveTab("Day");

    const timezone = locationDetail?.location_timezone || moment.tz.guess();
    const now = moment.tz(timezone);
    const startDateTime = moment.tz(`${now.format("YYYY-MM-DD")} ${inData?.startTime}`, "YYYY-MM-DD hh:mm A", timezone).seconds(0).milliseconds(0).utc().toISOString();
    const endDateTime = moment.tz(`${now.clone().endOf("day").format("YYYY-MM-DD")} ${inData?.endTime}`, "YYYY-MM-DD hh:mm A", timezone).seconds(59).milliseconds(999).utc().toISOString();
    setSelectedDates([{ startDateTime, endDateTime }]);
  };



  // ================================
  // EFFECTS 
  // ================================

  useEffect(() => {
    setInitialStartTime();
  }, [locationDetail?.location_timezone]);

  useEffect(() => {
    if (isMonthlySubscription && locationDetail?.location_timezone) {
      handleMonthlySubscriptionClick();
    }
  }, [locationDetail?.location_timezone]);


  return (

    isExtendParking ?
      <ExtendParking
        isMonthlySubscription={isMonthlySubscription}
        setIsMonthlySubscription={setIsMonthlySubscription}
        setSelectedBookingType={setSelectedBookingType}
        extensionDuration={extensionDuration}
        setExtensionDuration={setExtensionDuration}
        subscriptionAmount={locationDetail?.location_price[0]?.monthlySubscriptionPrice?.price}
      /> :

      <section className="card mt-3 schedule-card">
        {/* HEADER */}

        <div className="section-header justify-content-between">
          <div className="left">
            <BsFillCalendar2DateFill size={28} color="#2563eb" />

            <h4>Schedule</h4>
          </div>

          {/* <Button
            variant={isMonthlySubscription ? "success" : "primary"}
            className="rounded"
            onClick={isMonthlySubscription ? handleCancelSubscriptionClick : handleMonthlySubscriptionClick}
          >
            Monthly Subscription
          </Button> */}



        </div>

        <p className="sub-text">Select date & time</p>

        <Card
          className="border border-dark mb-3 w-100 rounded-3"
        >
          <Card.Body className="p-0">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Badge pill className="p-2"> 🔥 Best Value </Badge>
              <Form.Check
                type="switch"
                checked={isMonthlySubscription}
                onChange={() => {
                  if (isMonthlySubscription) {
                    handleCancelSubscriptionClick();
                  } else {
                    handleMonthlySubscriptionClick();
                  }
                }}
                className="m-0 fs-3"
              />
            </div>

            {/* Title */}
            <Card.Title className="fw-bold fs-4 mb-3">
              Monthly Subscription
            </Card.Title>
            {/* Features + Price */}
            <div className="d-flex gap-3 align-items-end align-items-sm-start subscription-card">
              <ul className="list-unstyled flex-grow-1 mb-0 small">
                <li className="mb-1"> <span style={{ color: "#00c8ff" }}>✓ </span> Guaranteed spot — always available</li>
                <li className="mb-1"> <span style={{ color: "#00c8ff" }}>✓ </span> Unlimited entry & exit (24/7 access)</li>
                <li className="mb-1"> <span style={{ color: "#00c8ff" }}>✓ </span> Members-only perks & discounts</li>
                <li className="mb-1"> <span style={{ color: "#00c8ff" }}>✓ </span> No daily booking required</li>
                <li className="mb-1"> <span style={{ color: "#00c8ff" }}>✓ </span> Best value for frequent drivers</li>
              </ul>

              <Badge className="fs-4 fw-bold px-3 py-2 flex-shrink-0 rounded-pill" >
                ${locationDetail?.location_price[0]?.monthlySubscriptionPrice?.overridePrice || '0'}<small className="mb-3" style={{ fontSize: 13 }}> /month</small>
              </Badge>
            </div>
          </Card.Body>
        </Card>

        {/* TABS */}

        <div className="d-flex justify-content-sm-between align-items-sm-center gap-2 flex-column-reverse flex-sm-row justify-content-end align-items-end ">
          <div className="tabs w-100 justify-content-sm-start justify-content-between">
            {["Day", "Weekly", "Monthly"].map((tab) => (
              <button
                key={tab}
                className={activeTab === tab ? "active" : ""}
                onClick={() => {
                  setActiveTab(tab);
                  setIsMonthlySubscription(false);
                  setRange({
                    start: null,
                    end: null,
                  });

                  let startDateTime, endDateTime;
                  const timezone = locationDetail?.location_timezone || moment.tz.guess();
                  const now = moment.tz(timezone); // Get current time IN the target timezone


                  if (tab === "Day") {
                    startDateTime = moment.tz(`${now.format("YYYY-MM-DD")} ${inData?.startTime}`, "YYYY-MM-DD hh:mm A", timezone)
                      .seconds(0).milliseconds(0).utc().toISOString();
                    endDateTime = moment.tz(`${now.format("YYYY-MM-DD")} ${inData?.endTime}`, "YYYY-MM-DD hh:mm A", timezone)
                      .seconds(59).milliseconds(999).utc().toISOString();

                  } else if (tab === "Weekly") {
                    startDateTime = moment.tz(`${now.format("YYYY-MM-DD")} ${inData?.startTime}`, "YYYY-MM-DD hh:mm A", timezone)
                      .seconds(0).milliseconds(0).utc().toISOString();
                    endDateTime = moment.tz(`${now.clone().add(6, "days").format("YYYY-MM-DD")} ${inData?.endTime}`, "YYYY-MM-DD hh:mm A", timezone)
                      .seconds(59).milliseconds(999).utc().toISOString();

                  } else if (tab === "Monthly") {
                    startDateTime = moment.tz(`${now.format("YYYY-MM-DD")} ${inData?.startTime}`, "YYYY-MM-DD hh:mm A", timezone)
                      .seconds(0).milliseconds(0).utc().toISOString();

                    endDateTime = moment.tz(`${now.clone().add(1, "month").endOf("day").format("YYYY-MM-DD")} ${inData?.endTime}`, "YYYY-MM-DD hh:mm A", timezone)
                      .seconds(59).milliseconds(999).utc().toISOString();
                  }

                  setSelectedDates([{ startDateTime, endDateTime }]);
                  setSelectedBookingType(tab.toUpperCase());
                }}
              >
                {tab}
              </button>
            ))}
          </div>
          {isMonthlySubscription && <Button
            name="confirm-schedule"
            onClick={() => setConfirm(true)}
            variant="primary"
            className="fw-bold"
            size="sm"
          >Confirm</Button>}
        </div>

        {!isMonthlySubscription &&
          <div className="schedule-grid">
            {/* CALENDAR */}

            <div className="calendar-box">
              <div className="calendar-header">
                <ChevronLeft onClick={() => changeMonth(-1)} />

                <span>
                  {currentDate.toLocaleString("default", {
                    month: "long",
                  })}{" "}
                  {currentDate.getFullYear()}
                </span>

                <ChevronRight onClick={() => changeMonth(1)} />
              </div>

              <div className="week-row">
                {days.map((d) => (
                  <span key={d}>{d}</span>
                ))}
              </div>

              <div className="date-grid">
                {calendarDays.map((day, i) => {
                  const past = isPastDate(day);

                  return (
                    <div
                      key={i}
                      className={`date
                    ${!day ? "empty" : ""}
                    ${past ? "disabled" : ""}
                    ${isInRange(day) ? "range" : ""}
                    ${isStartOrEnd(day) ? "active" : ""}
                  `}
                      onClick={() => handleDateSelect(day)}
                    >
                      {day || ""}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* TIME */}

            <div className="time-section">

              {selectedDates?.length > 0 && <div className="time-box">
                <label>Start Date</label>

                <div className="time-input">
                  <CalendarEvent size={16} />
                  <input
                    type="date"
                    name="date"
                    value={
                      selectedDates[0]?.startDateTime
                        ? moment(selectedDates[0].startDateTime).format("YYYY-MM-DD")
                        : ""}
                    readOnly
                    disabled
                  />
                </div>
              </div>}

              <div className="time-box">
                <label>Start Time</label>

                <div className="time-input">
                  <Clock size={16} />

                  <input
                    type="time"
                    name="startTime"
                    value={convertTo24(inData?.startTime)}
                    onChange={handleTimeChange}
                  />
                </div>
              </div>

              <div className="time-box">
                <label>End Time</label>

                <div className="time-input">
                  <Clock size={16} />

                  <InputField
                    value={convertTo24(inData?.endTime)}
                    type={"time"}
                    name={"endTime"}
                    disabled={true}
                    className={"py-0"}
                  />
                </div>
              </div>

              <Button
                name="confirm-schedule"
                onClick={() => setConfirm(true)}
                variant="primary"
                className="fw-bold mt-3"
                size="sm"
              >Confirm</Button>

            </div>
          </div>}

      </section>

  );
}

export default React.memo(ScheduleSection);

